/**
 * interruptedTurn
 *
 * Point 6 fix — mid-stream hard-refresh DATA-LOSS (in-flight turn dropped).
 *
 * Background
 * ──────────────────────────────────────────────────────────────────────
 * This module handles hydrate-time reconciliation for orphaned in-flight UI
 * placeholders. There are now two execution paths:
 *   1) Legacy guest/client path (can run entirely in-tab), and
 *   2) Migrated VM/account path (durable server-side task via taskId).
 *
 * On the migrated VM tenant, deep research runs as a durable server-side job
 * that survives refresh and is re-attached by the resume effects in
 * useGlobalChat.ts. This file intentionally stays agnostic: messages carrying
 * a `serverTaskId` are treated as resumable (not dead orphans) and left for
 * the resume path to finalize.
 *
 * The in-flight assistant placeholder DOES get persisted to the per-thread
 * store + Firestore (it carries the animated `getPendingWidgetHtml(...)`
 * widget and at most a stage label as text), and it DOES rehydrate into
 * `state.messages` on reload. The bug is purely a RENDER outcome: the
 * renderer (GlobalChatSystem.tsx) treats any message whose html contains the
 * `pendingShimmer` / `pendingSpin` animation as a live "thinking" widget and
 * shows it as a floating, forever-spinning indicator — which can never
 * finalize because the client run is gone. To the user: a dead spinner, then
 * the same dead spinner on a second refresh. The answer is permanently lost
 * with no signal that anything is wrong.
 *
 * Fix (client-only, safest)
 * ──────────────────────────────────────────────────────────────────────
 * On EVERY thread (re)hydrate boundary — where, by definition, no live client
 * run exists yet — detect an orphaned in-flight assistant widget and convert
 * it into a clearly-marked `'interrupted'` message: a calm, NON-animated
 * notice plus a one-click "Regenerate" affordance whose prompt is the
 * immediately-preceding user bubble (already persisted). This eliminates the
 * data-loss illusion (no dead spinner; the turn is visibly marked
 * interrupted) and preserves the prompt for a single-tap re-run.
 *
 * It does NOT auto-resume the research run — that lives in the destroyed tab
 * and requires a server-side durable job (out of scope; the real long-term
 * fix). See PLAN-point6-inflight-turn-persist.md.
 *
 * This module is PURE (no React, no storage, no Firestore) so it is trivially
 * unit-testable and safe to call inside the synchronous hydrate dispatch
 * sites in `useChatThreads`.
 */

import type { ChatMessage, TokenUsageMetadata } from './types';
import { deriveTaskAnchorId } from '../../types/taskAnchor';
import { isLiveSendInFlight } from './liveSendRegistry';
import { normalizeAnswerTextForDedupe } from './answerDedupeNormalize';
import { taskMintTimestampMs } from './serverTaskResume';
import { isServerUserTwinId, sharedIdOf } from '../../services/groupThreads/userRowTwins';

/**
 * Substrings that mark a live pending/streaming animation in a message's
 * html (produced by `getPendingWidgetHtml`). If html contains either, the
 * renderer treats the message as a live spinner. The interrupted-notice html
 * we emit deliberately contains NEITHER.
 */
export const PENDING_ANIMATION_MARKERS = ['pendingShimmer', 'pendingSpin'] as const;

/**
 * WS-4(b) (2026-09-20, docs/PLAN-mid-turn-fold-in-20260915.md §11.2): the OLDEST in-flight
 * placeholder that is still RE-ATTACHABLE. A placeholder carrying a `serverTaskId` is kept
 * alive across reloads so the resume poll can deliver the answer — but the resume finalizer
 * renders whatever the task's terminal record holds, a completed task's dead-end floor line
 * included, stamped with the RESUME's clock; so a placeholder from hours ago could paint a
 * "didn't get to a clean finish" line into a thread long after anyone was waiting. Past this
 * age the placeholder is an ORPHAN like any other: the hydrate reconcile marks it interrupted
 * at its ORIGINAL timestamp (the existing failure-class card, D9) and the resume scan never
 * offers it. Every real turn is minutes long; six hours is far outside any legitimate run.
 */
export const RESUMABLE_MAX_AGE_MS = 6 * 60 * 60 * 1000;

/**
 * Pure: was this server task minted longer ago than RESUMABLE_MAX_AGE_MS? A legacy or
 * foreign id (no mint stamp) is NOT expired — an unknown age keeps today's behaviour.
 */
export function serverTaskIsExpiredForResume(
  serverTaskId: string | null | undefined,
  nowMs: number,
): boolean {
  const mintMs = taskMintTimestampMs(serverTaskId);
  if (mintMs === null) return false;
  return nowMs - mintMs > RESUMABLE_MAX_AGE_MS;
}

/**
 * Known sourceTags used for in-flight pending/streaming placeholders. A
 * message with one of these tags that was never finalized is an orphaned
 * in-flight widget after a reload.
 */
export const PENDING_SOURCE_TAGS = new Set<string>([
  'pending',
  'immediate_thinking_pending',
  'stage1_assessment_pending',
  'attachment_read_pending',
  'pre_orchestrator_attachment_read_pending',
  'prest0n_session_streaming',
]);

/**
 * Transient status surfaces that must NEVER survive in persisted history
 * (2026-08-08 loading-UX fix). Older bundles emitted the cold-boot wait status
 * ('⏳ Starting your private Prest0n workspace…') and the 45s/180s progress
 * hints as REAL persisted messages; when their removal raced a thread switch,
 * refresh, or cross-tab sync they leaked into history forever — rendering as
 * a stale plain "boot" bubble or (for the animated hints) converting into
 * phantom "Interrupted" cards on the next hydrate. Current bundles update the
 * single pending widget in place instead of minting these messages; this
 * sweep retires the already-leaked ones wherever they still exist.
 */
export const TRANSIENT_STATUS_SOURCE_TAGS = new Set<string>([
  'prest0n_session_booting',
  'webchat_streaming_hint_45s',
  'webchat_streaming_hint_180s',
]);

/**
 * Drop leaked transient status messages (see TRANSIENT_STATUS_SOURCE_TAGS).
 * Pure; returns the ORIGINAL array reference when nothing was dropped.
 */
export function dropTransientStatusMessages(messages: ChatMessage[]): ChatMessage[] {
  if (!Array.isArray(messages) || messages.length === 0) return messages;
  const out = messages.filter(
    (m) => !(m && !m.isUser && !!m.sourceTag && TRANSIENT_STATUS_SOURCE_TAGS.has(m.sourceTag)),
  );
  return out.length === messages.length ? messages : out;
}

/** True when the html carries a live pending animation. */
function htmlHasPendingAnimation(html?: string): boolean {
  if (!html) return false;
  return PENDING_ANIMATION_MARKERS.some((marker) => html.includes(marker));
}

/**
 * ONE shared predicate for "this assistant message is still an in-flight
 * placeholder/streaming widget" (2026-07-31 one-reposition-per-turn fix).
 *
 * A message is in-flight when it is a non-settled assistant bubble carrying
 * any live signal: `status:'streaming'`, a pending animation in its html
 * (`getPendingWidgetHtml`), a pending sourceTag, or the onToolStart-rotated
 * `prest0n_tool_start_<toolId>` tag family. Interrupted and completed
 * messages are SETTLED (never in-flight); user bubbles never stream.
 *
 * Consumers must all share this exact definition so they can never drift:
 *   • deriveConvergentLastActiveAt (useChatThreads) excludes in-flight
 *     placeholders from the sidebar sort key — placeholder mints/swaps
 *     produce zero key steps, and every device excludes the SAME
 *     placeholders from the same transcript (convergence preserved);
 *   • remoteFinalizeShouldReleaseProcessing (below) detects when a merge
 *     retires a thread's last in-flight indication.
 */
export function isInFlightPlaceholderMessage(message: ChatMessage): boolean {
  if (!message || message.isUser) return false;
  if (message.interrupted || message.status === 'interrupted') return false;
  if (message.status === 'complete') return false;
  return (
    message.status === 'streaming'
    || htmlHasPendingAnimation(message.html)
    || (!!message.sourceTag && (
      PENDING_SOURCE_TAGS.has(message.sourceTag)
      || message.sourceTag.startsWith('prest0n_tool_start_')
    ))
  );
}

/**
 * A settled TERMINAL failure card (Interrupted / failed turn) with a real
 * body. Distinct from isFinalizedCopy (which deliberately excludes
 * interrupted messages): the durable single-writer failure surface persists
 * the Interrupted card server-side (persistTaskFailureToThread), and that
 * card must be able to RETIRE a local in-flight placeholder on sync —
 * otherwise the placeholder spins forever, remoteFinalizeShouldReleaseProcessing
 * never fires, and every later send on the thread queues behind a dead turn
 * (2026-08-14 Harbor Ledger repro: Regenerate stuck on "Queued — will send
 * when the current reply finishes" behind a task the server had already
 * failed).
 */
export function isTerminalInterruptedCopy(message: ChatMessage): boolean {
  if (!message || message.isUser) return false;
  if (!(message.interrupted || message.status === 'interrupted')) return false;
  return !!((message.text || '').trim() || (message.html || '').trim());
}

/**
 * True when NOTHING in the thread's messages carries a live in-flight signal
 * (no streaming status, no pending animation, no pending/tool-start
 * sourceTag). Used by the send path's queue gate as a wedge detector: a
 * thread flagged `processing` whose transcript shows no live turn is held by
 * a corpse, and queueing behind it parks the send until the 10-minute
 * safety floor. Pure.
 */
export function threadHasNoLiveTurn(messages: ChatMessage[]): boolean {
  if (!Array.isArray(messages) || messages.length === 0) return true;
  return !messages.some((m) => isInFlightPlaceholderMessage(m));
}

/**
 * The server task id behind the thread's LIVE in-flight turn, or null
 * (mid-turn interjection, 2026-08-16). Scans newest-first so a thread with
 * older settled task-backed answers still resolves the CURRENT turn. Only a
 * message that is BOTH in-flight and task-stamped qualifies — an SSE-lane
 * turn (no task doc) or a settled answer never yields an id, and the caller
 * then falls back to the ordinary queued-send path. Pure.
 */
export function liveServerTaskIdOf(messages: ChatMessage[]): string | null {
  if (!Array.isArray(messages)) return null;
  for (let i = messages.length - 1; i >= 0; i -= 1) {
    const m = messages[i];
    if (!m || m.isUser) continue;
    if (!isInFlightPlaceholderMessage(m)) continue;
    const taskId = typeof m.serverTaskId === 'string' ? m.serverTaskId.trim() : '';
    if (taskId) return taskId;
  }
  return null;
}

/**
 * A finalized assistant answer has real, non-trivial content. A pending
 * widget mid-deep-research has at most a short stage label as text (e.g.
 * "Step 2 of 3: deep researching the web…") and only the animated widget as
 * html. We treat a message as "has a finalized body" when it has html WITHOUT
 * the pending animation, OR it has a meaningful non-label text body. The
 * conservative test below biases toward NOT converting anything that already
 * looks finalized.
 */
export function hasFinalizedBody(message: ChatMessage): boolean {
  const html = message.html || '';
  if (html && !htmlHasPendingAnimation(html)) {
    // Non-animated html present → this is rendered content, not a spinner.
    return true;
  }
  // Data-only cards (renderPolicy:'never' — connect offer/sign-in cards): the
  // card html IS the finalized content (2026-08-22 owner report — these were
  // invisible to every merge/adopt path and never reached other devices).
  if ((message.workflowActionButtonsHtml || '').trim()) {
    return true;
  }

  const text = (message.text || '').trim();

  // No usable html. If there is substantial plain text that is NOT just a
  // stage label, treat it as a (text-only) finalized answer. Stage labels are
  // short; real answers are long. Use a conservative length threshold and the
  // absence of a pending sourceTag.
  // Durability audit rank #19 (2026-07-13): onToolStart rotates the pending
  // widget to sourceTag 'prest0n_tool_start_<toolId>' — those widgets were
  // invisible to every reconciler (never resumed, never marked interrupted):
  // a permanent dead-spinner variant on refresh. Prefix-match covers them.
  const isPendingTagged = !!message.sourceTag && (
    PENDING_SOURCE_TAGS.has(message.sourceTag) ||
    message.sourceTag.startsWith('prest0n_tool_start_')
  );
  // FIXED (Miles, 2026-06-14): Only apply the length-threshold shortcut when the message
  // is NOT tagged as pending — otherwise a server-task in-flight message like "deep researching"
  // (16 chars, pending-tagged) would score as finalized and be excluded from findResumableServerTasks.
  if (!isPendingTagged && text.length > 15) {
    return true;
  }
  if (!isPendingTagged && text.length > 0) {
    return true;
  }
  return false;
}

/**
 * Predicate: is `message` an orphaned in-flight assistant widget on reload
 * (a turn that was streaming when the tab was hard-refreshed and can never
 * finalize itself)?
 *
 * NOTE: this is only ever evaluated on the hydrate boundary, where no live
 * client run can exist — so a genuinely active stream is never misclassified.
 */
export function isOrphanedInFlightMessage(message: ChatMessage, nowMs: number = Date.now()): boolean {
  if (!message || typeof message !== 'object') return false;
  // Only assistant messages stream; user bubbles are never in-flight widgets.
  if (message.isUser) return false;
  // Already reconciled — idempotent.
  if (message.interrupted || message.status === 'interrupted') return false;

  // Point 6 (Option B) refresh-survival: a message carrying a serverTaskId was
  // executed SERVER-SIDE (durable guest deep research). The server task kept
  // running through the refresh, so this is NOT a dead orphan — it is
  // RE-ATTACHABLE. Do NOT mark it interrupted; useGlobalChat resumes polling
  // guestPollTask(serverTaskId) and finalizes it. (If the task is genuinely
  // gone/errored, the resume path is what flips it to a normal error/
  // interrupted card — never this hydrate pass.)
  if (!hasFinalizedBody(message) && typeof message.serverTaskId === 'string' && message.serverTaskId) {
    // WS-4(b) (2026-09-20): …unless the task was minted longer ago than any turn can run.
    // Then it is a dead orphan after all, and falls through to the pending-widget tests
    // below like a placeholder that never had a server task.
    if (!serverTaskIsExpiredForResume(message.serverTaskId, nowMs)) return false;
  }

  const taggedPending = !!message.sourceTag && PENDING_SOURCE_TAGS.has(message.sourceTag);
  const animated = htmlHasPendingAnimation(message.html);

  // Must look like a pending widget by either signal …
  if (!taggedPending && !animated) return false;
  // … and must NOT already carry a finalized answer body.
  if (hasFinalizedBody(message)) return false;

  return true;
}

/** HTML-escape for safe embedding of the prompt preview into the notice. */
function escapeHtml(value: string): string {
  return value
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');
}

// copy: interrupted_turn
export const INTERRUPTED_NOTICE_TEXT =
  'This answer was interrupted by a page refresh and could not finish. Tap Regenerate to run it again.';

/**
 * Build the interrupted-notice html. CRITICAL: this must contain NEITHER
 * `pendingShimmer` NOR `pendingSpin`, so the renderer never re-treats it as a
 * live spinner (that is the exact regression this whole fix closes). It is a
 * calm, static notice. The `data-regenerate-prompt` hook lets the renderer
 * wire up a one-click re-run.
 */
export function buildInterruptedHtml(promptPreview?: string): string {
  const preview = (promptPreview || '').trim();
  const previewBlock = preview
    ? `<div class="interrupted-turn-prompt" style="margin-top:8px;font-size:12px;color:#6b6b6b;font-style:italic;">${escapeHtml(
        preview.length > 200 ? `${preview.slice(0, 200)}…` : preview,
      )}</div>`
    : '';
  return [
    `<div class="interrupted-turn-notice" role="status" data-interrupted="true"${
      preview ? ` data-regenerate-prompt="${escapeHtml(preview)}"` : ''
    } style="display:flex;flex-direction:column;gap:4px;padding:12px 16px;border-radius:12px;background:#FBF7EE;border:1px solid rgba(198,167,94,0.25);font-family:'Inter',-apple-system,BlinkMacSystemFont,sans-serif;font-size:13px;color:#8a6d2f;">`,
    `<span style="font-weight:600;">Interrupted</span>`,
    `<span>${escapeHtml(INTERRUPTED_NOTICE_TEXT)}</span>`,
    previewBlock,
    `</div>`,
  ].join('');
}

/**
 * Convert an orphaned in-flight assistant message into a clearly-marked
 * interrupted message. Returns a NEW object (never mutates the input).
 * Preserves id / timestamp / sourceTag for history continuity, strips the
 * animated html, and stamps `regeneratePrompt`.
 */
export function markInterrupted(
  message: ChatMessage,
  opts: { regeneratePrompt?: string } = {},
): ChatMessage {
  const regeneratePrompt = (opts.regeneratePrompt || '').trim() || undefined;
  return {
    ...message,
    status: 'interrupted',
    interrupted: true,
    regeneratePrompt,
    text: INTERRUPTED_NOTICE_TEXT,
    html: buildInterruptedHtml(regeneratePrompt),
    // CRITICAL: clear the pending/streaming sourceTag. The renderer diverts
    // any message with a pending sourceTag + pending-animation html into the
    // floating "thinking" overlay (GlobalChatSystem.tsx isPendingOverlayCandidate),
    // and other logic special-cases these tags. Once interrupted, this is a
    // normal recoverable bubble — strip the tag so it can never be re-treated
    // as a live pending widget/overlay (the exact dead-spinner regression).
    sourceTag: 'interrupted_turn',
  };
}

/**
 * Single pure pass over a hydrated messages array. For each orphaned in-flight
 * assistant message, find the nearest PRECEDING user message's text as the
 * regenerate prompt and replace it via `markInterrupted`.
 *
 * Returns the ORIGINAL array reference when nothing changed (stable identity
 * for React diffing — mirrors `sanitizeMessagesForRehydrate`).
 */
export function reconcileInterruptedOnHydrate(
  messages: ChatMessage[],
  nowMs: number = Date.now(),
): { messages: ChatMessage[]; changed: boolean } {
  if (!Array.isArray(messages) || messages.length === 0) {
    return { messages, changed: false };
  }

  // Retire leaked transient status bubbles FIRST — an animated hint bubble
  // would otherwise be misread as an orphaned turn and converted into a
  // phantom "Interrupted" card below.
  const swept = dropTransientStatusMessages(messages);
  const sweptChanged = swept !== messages;
  if (swept.length === 0) {
    return { messages: swept, changed: sweptChanged };
  }

  let changed = sweptChanged;
  const out = swept.map((message, index) => {
    if (!isOrphanedInFlightMessage(message, nowMs)) return message;

    // Find nearest preceding user bubble for the regenerate prompt.
    let regeneratePrompt: string | undefined;
    for (let j = index - 1; j >= 0; j -= 1) {
      const prior = swept[j];
      if (prior && prior.isUser && (prior.text || '').trim()) {
        regeneratePrompt = (prior.text || '').trim();
        break;
      }
    }

    changed = true;
    return markInterrupted(message, { regeneratePrompt });
  });

  // ONE orphaned turn → ONE Interrupted card (owner screenshot 2026-08-16,
  // starter-chip incognito repro: the turn's TWO orphaned placeholders — the
  // immediate thinking widget AND the handler's stage1/tool-start widget,
  // whose in-place swap raced a thread transition so both persisted —
  // each converted above into an identical "Interrupted" card). Collapse
  // duplicates within the turn window before returning; also heals cards a
  // pre-fix double-run already persisted, so the pass stays idempotent.
  const converted = changed ? out : swept;
  const deduped = dedupeTurnWindowInterruptedCards(converted);
  if (deduped !== converted) changed = true;
  return changed ? { messages: deduped, changed: true } : { messages, changed: false };
}

/**
 * BUG-FIX (mid-stream refresh QA regression, 2026-07): dedupe an already-
 * merged messages array by `id`, keeping the "most complete" instance of any
 * duplicate id. Two writers can race to persist the SAME turn under the SAME
 * message id around a refresh (hydrate-from-storage `incoming` + the
 * in-memory `preserved` tail from before the reload) and, without this pass,
 * BOTH copies survive the merge and render as two identical bubbles.
 *
 * "Most complete" wins over "still pending/interrupted": a message with a
 * finalized body (see `hasFinalizedBody`) or `status === 'complete'` beats a
 * pending/interrupted duplicate with the same id. Ties keep the LAST instance
 * encountered (i.e. the more recently-merged copy), which mirrors prior
 * behavior for non-duplicated ids. Messages without an `id` are never
 * deduped (can't be reliably identified) and pass through unchanged, in
 * their original relative order.
 *
 * Pure, no React/storage — safe to call from any hydrate/merge boundary.
 */
/**
 * Guard a synced snapshot against DOWNGRADING an already-finalized bubble
 * (Symptom 2, Miles 2026-07-10: a deep-research report appears, then vanishes).
 *
 * Root cause: same-tab/cross-tab sync paths (the BroadcastChannel re-read in
 * useChatThreads, storage rehydrate, etc.) dispatch SET_MESSAGES from a
 * per-thread snapshot that can lag the just-finalized in-memory state by a few
 * hundred ms. Those paths only guarded against an EMPTY snapshot wiping a
 * non-empty view — they did NOT guard against a stale snapshot replacing a
 * finalized report bubble with its earlier PENDING/placeholder version for the
 * SAME message id. The report renders on finalize, then the lagging snapshot
 * overwrites it → the report vanishes.
 *
 * This pure guard walks the incoming (synced) list and, for any message id
 * that ALSO exists in the current in-memory list where the current copy has a
 * finalized body but the incoming copy does NOT, keeps the current (finalized)
 * copy. It never adds/removes/reorders ids — it only prevents a per-id
 * content DOWNGRADE. Empty/absent ids are passed through untouched. This is
 * non-mutating and safe to run on every sync dispatch.
 */
export function mergeSyncedMessagesPreservingFinalized(
  incoming: ChatMessage[],
  current: ChatMessage[],
): ChatMessage[] {
  if (!Array.isArray(incoming) || incoming.length === 0) return incoming;
  if (!Array.isArray(current) || current.length === 0) return incoming;

  const currentById = new Map<string, ChatMessage>();
  for (const msg of current) {
    if (msg?.id) currentById.set(msg.id, msg);
  }
  if (currentById.size === 0) return incoming;

  let changed = false;
  const merged = incoming.map((msg) => {
    const id = msg?.id;
    if (!id) return msg;
    const cur = currentById.get(id);
    if (!cur) return msg;
    // Preserve the current finalized copy only when the incoming copy would
    // be a strict downgrade: current is finalized+not-interrupted, incoming
    // is neither finalized nor interrupted (i.e. pending/placeholder/empty).
    //
    // A live pending WIDGET (animated html) or a pending SOURCE TAG is a
    // definitive "not finalized" signal even when a short stage-label text
    // ("Step 2 of 3: deep researching…") would otherwise trip the
    // length-based heuristic in hasFinalizedBody. Treat either as pending.
    const incomingPendingWidget = htmlHasPendingAnimation(msg.html)
      || (!!msg.sourceTag && PENDING_SOURCE_TAGS.has(msg.sourceTag));
    const curFinal = hasFinalizedBody(cur) && !cur.interrupted;
    const incomingFinal = hasFinalizedBody(msg) && !msg.interrupted && !incomingPendingWidget;
    const incomingInterrupted = msg.status === 'interrupted' || !!msg.interrupted;
    if (curFinal && !incomingFinal && !incomingInterrupted) {
      changed = true;
      return cur;
    }
    return msg;
  });

  return changed ? merged : incoming;
}

export function dedupeMessagesById(messages: ChatMessage[]): ChatMessage[] {
  if (!Array.isArray(messages) || messages.length === 0) return messages;

  const bestById = new Map<string, { message: ChatMessage; index: number }>();
  const withoutId: Array<{ message: ChatMessage; index: number }> = [];
  let sawDuplicate = false;

  const completionRank = (m: ChatMessage): number => {
    if (m.status === 'complete' && !m.interrupted) return 4;
    if (hasFinalizedBody(m) && !m.interrupted) return 3;
    if (m.status === 'interrupted' || m.interrupted) return 0;
    return 2;
  };

  const bodyLength = (m: ChatMessage): number => {
    const textLen = (m.text || '').trim().length;
    const htmlLen = (m.html || '').trim().length;
    return Math.max(textLen, htmlLen);
  };

  const timestampMs = (m: ChatMessage): number => {
    const t = m.timestamp instanceof Date
      ? m.timestamp.getTime()
      : new Date(m.timestamp as unknown as string).getTime();
    return Number.isFinite(t) ? t : Number.NEGATIVE_INFINITY;
  };

  const shouldReplace = (
    candidate: { message: ChatMessage; index: number },
    current: { message: ChatMessage; index: number },
  ): boolean => {
    const candidateRank = completionRank(candidate.message);
    const currentRank = completionRank(current.message);
    if (candidateRank !== currentRank) return candidateRank > currentRank;

    const candidateBodyLen = bodyLength(candidate.message);
    const currentBodyLen = bodyLength(current.message);
    if (candidateBodyLen !== currentBodyLen) return candidateBodyLen > currentBodyLen;

    const candidateTs = timestampMs(candidate.message);
    const currentTs = timestampMs(current.message);
    if (candidateTs !== currentTs) return candidateTs > currentTs;

    // Final tie-breaker: keep the later encountered instance.
    return candidate.index >= current.index;
  };

  messages.forEach((message, index) => {
    const id = message?.id;
    if (!id) {
      withoutId.push({ message, index });
      return;
    }

    const candidate = { message, index };
    const existing = bestById.get(id);
    if (!existing) {
      bestById.set(id, candidate);
      return;
    }

    sawDuplicate = true;
    if (shouldReplace(candidate, existing)) {
      bestById.set(id, candidate);
    }
  });

  if (!sawDuplicate) {
    return messages;
  }

  // Re-merge, preserving original relative ordering by index.
  const kept: Array<{ message: ChatMessage; index: number }> = [
    ...Array.from(bestById.values()),
    ...withoutId,
  ];
  kept.sort((a, b) => a.index - b.index);
  return kept.map((entry) => entry.message);
}

/**
 * BUG-FIX (false "Interrupted" card surviving a real completed answer,
 * 2026-07): once a message is marked `interrupted` via `markInterrupted`, the
 * hydrate reconcile pass is idempotent and never re-examines it — so if a
 * genuinely-finished answer for the SAME turn lands afterward (e.g. a
 * still-running server-side stream that completes and gets persisted under
 * the SAME message id, or the merge in `useGlobalChat` momentarily carried
 * both an interrupted copy and a later completed copy of the same id before
 * `dedupeMessagesById` ran), the stale "Interrupted / Tap Regenerate" card
 * keeps rendering above the real, completed answer.
 *
 * This pass finds any `interrupted` message whose immediately-following
 * assistant message responds to the SAME preceding user prompt and is itself
 * finalized (non-interrupted, non-pending). In that case the stale
 * interrupted message is dropped so only the real answer remains.
 */
export function clearStaleInterruptedCards(messages: ChatMessage[]): { messages: ChatMessage[]; changed: boolean } {
  if (!Array.isArray(messages) || messages.length === 0) {
    return { messages, changed: false };
  }

  const toDrop = new Set<number>();

  const getPrecedingUserInfo = (
    assistantIndex: number,
  ): { index: number; id?: string; text: string } | null => {
    for (let j = assistantIndex - 1; j >= 0; j -= 1) {
      const prior = messages[j];
      if (prior && prior.isUser) {
        return {
          index: j,
          id: prior.id,
          text: (prior.text || '').trim(),
        };
      }
    }
    return null;
  };

  const getTaskId = (message: ChatMessage): string | undefined => {
    const taskId = typeof message.serverTaskId === 'string' ? message.serverTaskId.trim() : '';
    return taskId || undefined;
  };

  messages.forEach((message, index) => {
    if (!message || message.isUser) return;
    if (!(message.status === 'interrupted' || message.interrupted)) return;

    const interruptedPrompt = getPrecedingUserInfo(index);
    const interruptedTaskId = getTaskId(message);

    // No turn identity signal; can't safely infer staleness.
    if (!interruptedPrompt && !interruptedTaskId) return;

    // Look forward for a later, completed assistant answer for the SAME turn:
    //   1) same preceding user prompt message, OR
    //   2) same serverTaskId.
    for (let k = index + 1; k < messages.length; k += 1) {
      const later = messages[k];
      if (!later || later.isUser) continue;
      if (later.status === 'interrupted' || later.interrupted) continue;
      if (!hasFinalizedBody(later)) continue;

      const laterPrompt = getPrecedingUserInfo(k);
      const laterTaskId = getTaskId(later);

      const sameTaskId = !!(interruptedTaskId && laterTaskId && interruptedTaskId === laterTaskId);

      const samePromptMessage = !!(
        interruptedPrompt
        && laterPrompt
        && (
          (interruptedPrompt.id && laterPrompt.id && interruptedPrompt.id === laterPrompt.id)
          || interruptedPrompt.index === laterPrompt.index
          || (
            !interruptedPrompt.id
            && !laterPrompt.id
            && interruptedPrompt.text.length > 0
            && interruptedPrompt.text === laterPrompt.text
          )
        )
      );

      if (sameTaskId || samePromptMessage) {
        toDrop.add(index);
        break;
      }
    }
  });

  if (toDrop.size === 0) return { messages, changed: false };
  return {
    messages: messages.filter((_, index) => !toDrop.has(index)),
    changed: true,
  };
}

/**
 * BUG-FIX (mid-stream refresh QA regression round 2, 2026-07-02): a single
 * normalization pass combining `dedupeMessagesById` + `clearStaleInterruptedCards`.
 *
 * The first-round fix only ran these guards on the `SET_MESSAGES` reducer path
 * and the force-sync merge. But after a mid-stream refresh the REAL answer is
 * finalized through `UPDATE_MESSAGE_BY_ID` / `ADD_MESSAGE` (the resume path in
 * useGlobalChat) — which never re-scan the full array. Result: the hydrated
 * `interrupted` card and the newly-finalized answer coexist (false Interrupted
 * card above the real answer), and a duplicate answer appended for the same
 * turn survives (double bubble). Running this normalizer at EVERY
 * message-mutating reducer boundary closes both, regardless of which action
 * landed the answer.
 *
 * Returns the ORIGINAL array reference when nothing changed (stable identity
 * for React diffing).
 */
export function normalizeMessageList(messages: ChatMessage[]): ChatMessage[] {
  if (!Array.isArray(messages) || messages.length === 0) return messages;
  const swept = dropTransientStatusMessages(messages);
  const anchored = freezeTaskAnchors(swept);
  const deduped = dedupeMessagesById(anchored);
  const contentDeduped = dedupeAdjacentIdenticalAnswers(deduped);
  // 2026-08-14: card-separated duplicates (reply → connect card → same reply)
  // are invisible to the adjacent pass — the windowed pass collapses them.
  const windowDeduped = dedupeTurnWindowIdenticalAnswers(contentDeduped);
  // 2026-08-16 (starter-chip incognito repro): duplicate INTERRUPTED cards for
  // the same turn — every id/answer dedupe above deliberately skips
  // interrupted messages, so two orphaned placeholders converted into two
  // identical cards survived every boundary. Collapsed here so ANY path that
  // lands duplicates (double reconcile run, storage merge) self-heals.
  const interruptedDeduped = dedupeTurnWindowInterruptedCards(windowDeduped);
  const cleared = clearStaleInterruptedCards(interruptedDeduped);
  return cleared.changed ? cleared.messages : interruptedDeduped;
}

/**
 * CONVERGENCE ANCHOR freeze (Phase 3 read-cutover, Finding 2). The moment a
 * message carries a `serverTaskId`, capture a durable `taskAnchorId`
 * (`prest0n_task_<serverTaskId>`) that will NOT be cleared on finalize (unlike
 * serverTaskId, which finalize drops so resume won't re-attach). This is the
 * write-once handle every per-message-doc writer keys on, so the client bubble
 * and the server completion write converge on ONE doc. Runs at every reducer
 * boundary, so whichever stamp site set serverTaskId, the anchor is frozen on
 * the next pass — and survives the finalize that clears serverTaskId. Pure;
 * returns the original array reference when every message already has its anchor.
 */
function freezeTaskAnchors(messages: ChatMessage[]): ChatMessage[] {
  let changed = false;
  const out = messages.map((m) => {
    if (!m || m.taskAnchorId) return m;
    const anchor = deriveTaskAnchorId(m.serverTaskId);
    if (!anchor) return m;
    changed = true;
    return { ...m, taskAnchorId: anchor };
  });
  return changed ? out : messages;
}

/**
 * BUG-FIX (mid-stream refresh QA regression round 2, 2026-07-02): collapse
 * adjacent, identical completed assistant answers that carry DIFFERENT
 * message ids.
 *
 * `dedupeMessagesById` only catches same-id duplicates. But the refresh-resume
 * path can finalize the answer under a FRESH id (ADD_MESSAGE) while the copy
 * hydrated from storage keeps its original id — two different ids, identical
 * body, rendered as a double bubble (the QA "Hello" screenshot: two identical
 * replies, same 6.9s latency, same timestamp).
 *
 * Round K (duplicate-answer convergence, 2026-07): the async dedicated-VM turn
 * persisted the SAME answer twice under different html shapes — the client
 * finalize renders markdown→html while the server's durable copy carries the
 * raw body — so the original byte-identical text AND html requirement never
 * collapsed the pair. Compare NORMALIZED TEXT only (trim + collapse
 * whitespace; html equality remains solely as the fallback for text-less
 * html-only bodies), prefer keeping the ANCHORED copy (taskAnchorId /
 * serverTaskId present) so the survivor converges with the server's doc, and
 * preserve the earlier timestamp so ordering stays stable. Two messages
 * carrying DIFFERENT non-empty anchors are two genuinely different turns that
 * happened to produce identical text — never collapsed.
 *
 * Conservative by construction: only collapses two ASSISTANT messages that are
 * ADJACENT (ignoring nothing between them) and BOTH finalized
 * (non-interrupted, non-pending, with a finalized body). Legitimately repeated
 * answers are never adjacent+identical in normal use. Ties keep the first
 * instance (stable id for any in-flight references). Pure; returns original
 * ref when nothing changes.
 */
export function dedupeAdjacentIdenticalAnswers(messages: ChatMessage[]): ChatMessage[] {
  if (!Array.isArray(messages) || messages.length < 2) return messages;

  // INCIDENT-20260901: the shared normalizer also strips trailing
  // code-authored suffixes (the legacy server-appended version-fact line), so
  // a durable copy that differs from the live answer by that one line still
  // reads as the same answer and the pair collapses.
  const normalizedText = (m: ChatMessage): string => normalizeAnswerTextForDedupe(m.text);

  const isFinalizedAnswer = (m: ChatMessage): boolean =>
    !!m && !m.isUser && !m.interrupted && m.status !== 'interrupted' && hasFinalizedBody(m);

  const anchorOf = (m: ChatMessage): string => {
    const frozen = typeof m.taskAnchorId === 'string' ? m.taskAnchorId.trim() : '';
    if (frozen) return frozen;
    return deriveTaskAnchorId(m.serverTaskId) || '';
  };

  const timestampMs = (m: ChatMessage): number => {
    const t = m.timestamp instanceof Date
      ? m.timestamp.getTime()
      : new Date(m.timestamp as unknown as string).getTime();
    return Number.isFinite(t) ? t : Number.NaN;
  };

  const isSameAnswerBody = (a: ChatMessage, b: ChatMessage): boolean => {
    const aText = normalizedText(a);
    if (aText) return aText === normalizedText(b);
    if (normalizedText(b)) return false;
    // Text-less bodies (attachment/html-only tiles): fall back to the original
    // byte-identical html comparison — never collapse two empty bodies.
    const aHtml = (a.html || '').trim();
    return !!aHtml && aHtml === (b.html || '').trim();
  };

  let changed = false;
  const out: ChatMessage[] = [];
  for (const cur of messages) {
    const prev = out.length > 0 ? out[out.length - 1] : null;
    if (prev && isFinalizedAnswer(prev) && isFinalizedAnswer(cur) && isSameAnswerBody(prev, cur)) {
      const prevAnchor = anchorOf(prev);
      const curAnchor = anchorOf(cur);
      // DIFFERENT non-empty anchors → two distinct turns that happen to
      // produce identical text; keep both.
      if (!(prevAnchor && curAnchor && prevAnchor !== curAnchor)) {
        // Prefer the anchored copy (it converges with the server's durable
        // doc); ties keep the first instance (stable id, as before).
        const keep = !prevAnchor && curAnchor ? cur : prev;
        const other = keep === prev ? cur : prev;
        const keepMs = timestampMs(keep);
        const otherMs = timestampMs(other);
        const adoptEarlierTs = Number.isFinite(otherMs)
          && (!Number.isFinite(keepMs) || otherMs < keepMs);
        out[out.length - 1] = adoptEarlierTs ? { ...keep, timestamp: other.timestamp } : keep;
        changed = true;
        continue;
      }
    }
    out.push(cur);
  }

  return changed ? out : messages;
}

/**
 * 2026-08-14 (Harbor Ledger duplicate-reply repro: reply → Gmail connect card
 * → identical reply again): the adjacency-only dedupe above cannot collapse
 * identical answers when a NON-answer message (a connect card, a tile, a
 * system notice) lands between the copies — and the async turn has up to four
 * writers of the same body (live finalize, server durable write, client
 * recovery, group mirror) whose convergence otherwise rests entirely on the
 * task anchor. This windowed pass collapses identical finalized assistant
 * answers within ONE turn window (between consecutive user bubbles),
 * skipping over the in-between messages. The "two different non-empty
 * anchors ⇒ never collapse" rule is preserved, so genuinely distinct turns
 * that produce identical text survive; a regenerate starts a new window (its
 * user bubble resets the scan). Pure; returns the original array when
 * nothing collapsed.
 */
export function dedupeTurnWindowIdenticalAnswers(messages: ChatMessage[]): ChatMessage[] {
  if (!Array.isArray(messages) || messages.length < 3) return messages;

  // INCIDENT-20260901: the shared normalizer also strips trailing
  // code-authored suffixes (the legacy server-appended version-fact line), so
  // a durable copy that differs from the live answer by that one line still
  // reads as the same answer and the pair collapses.
  const normalizedText = (m: ChatMessage): string => normalizeAnswerTextForDedupe(m.text);
  const isFinalizedAnswer = (m: ChatMessage): boolean =>
    !!m && !m.isUser && !m.interrupted && m.status !== 'interrupted' && hasFinalizedBody(m);
  const anchorOf = (m: ChatMessage): string => {
    const frozen = typeof m.taskAnchorId === 'string' ? m.taskAnchorId.trim() : '';
    if (frozen) return frozen;
    return deriveTaskAnchorId(m.serverTaskId) || '';
  };
  const timestampMs = (m: ChatMessage): number => {
    const t = m.timestamp instanceof Date
      ? m.timestamp.getTime()
      : new Date(m.timestamp as unknown as string).getTime();
    return Number.isFinite(t) ? t : Number.NaN;
  };
  const isSameAnswerBody = (a: ChatMessage, b: ChatMessage): boolean => {
    const aText = normalizedText(a);
    if (aText) return aText === normalizedText(b);
    if (normalizedText(b)) return false;
    const aHtml = (a.html || '').trim();
    return !!aHtml && aHtml === (b.html || '').trim();
  };

  let changed = false;
  const out: ChatMessage[] = [];
  // Indexes (into `out`) of finalized answers seen since the last user bubble.
  let windowAnswerIdxs: number[] = [];
  for (const cur of messages) {
    if (cur?.isUser) {
      windowAnswerIdxs = [];
      out.push(cur);
      continue;
    }
    if (isFinalizedAnswer(cur)) {
      let collapsed = false;
      for (const seenIdx of windowAnswerIdxs) {
        const seen = out[seenIdx];
        if (!isSameAnswerBody(seen, cur)) continue;
        const seenAnchor = anchorOf(seen);
        const curAnchor = anchorOf(cur);
        if (seenAnchor && curAnchor && seenAnchor !== curAnchor) continue;
        // Same collapse semantics as the adjacent pass: prefer the anchored
        // copy, keep the earlier position, adopt the earlier timestamp.
        const keep = !seenAnchor && curAnchor ? cur : seen;
        const other = keep === seen ? cur : seen;
        const keepMs = timestampMs(keep);
        const otherMs = timestampMs(other);
        const adoptEarlierTs = Number.isFinite(otherMs)
          && (!Number.isFinite(keepMs) || otherMs < keepMs);
        out[seenIdx] = adoptEarlierTs ? { ...keep, timestamp: other.timestamp } : keep;
        changed = true;
        collapsed = true;
        break;
      }
      if (collapsed) continue;
      windowAnswerIdxs.push(out.length);
    }
    out.push(cur);
  }

  return changed ? out : messages;
}

/**
 * 2026-08-16 (owner incognito repro: starter chip → TWO identical
 * "Interrupted — …page refresh…" cards for ONE orphaned turn): collapse
 * duplicate INTERRUPTED cards within one turn window (between consecutive
 * user bubbles). One turn can legitimately leave more than one orphaned
 * placeholder behind — sendProgrammaticMessage's immediate thinking widget
 * plus the handler's stage1/tool-start widget, when the in-place swap raced a
 * thread transition and both persisted — and the hydrate reconciler then
 * mints one card PER placeholder. Both `dedupeMessagesById` (different ids)
 * and the identical-answer passes (`isFinalizedAnswer` excludes interrupted)
 * are blind to these, so without this pass the duplicates survive every
 * boundary forever.
 *
 * Collapse rule — deliberately narrow (a card is a user-facing terminal, so
 * only PROVABLE duplicates may merge):
 *   • both cards interrupted, in the SAME turn window;
 *   • identical normalized notice text AND identical regeneratePrompt
 *     (both-absent counts as identical — one orphaned turn, one Regenerate);
 *   • two DIFFERENT non-empty task anchors ⇒ two genuinely distinct failed
 *     turns that share a window (regenerate re-runs skip the user bubble) —
 *     never collapsed, mirroring the identical-answer passes' anchor rule.
 * Keeps the earlier card (stable id/position), preferring the anchored copy
 * and adopting the earlier timestamp — same semantics as the answer passes.
 * Pure; returns the original array reference when nothing collapsed.
 */
export function dedupeTurnWindowInterruptedCards(messages: ChatMessage[]): ChatMessage[] {
  if (!Array.isArray(messages) || messages.length < 2) return messages;

  const isInterruptedCard = (m: ChatMessage): boolean =>
    !!m && !m.isUser && (m.status === 'interrupted' || m.interrupted === true);
  // INCIDENT-20260901: the shared normalizer also strips trailing
  // code-authored suffixes (the legacy server-appended version-fact line), so
  // a durable copy that differs from the live answer by that one line still
  // reads as the same answer and the pair collapses.
  const normalizedText = (m: ChatMessage): string => normalizeAnswerTextForDedupe(m.text);
  const promptOf = (m: ChatMessage): string => (m.regeneratePrompt || '').trim();
  const anchorOf = (m: ChatMessage): string => {
    const frozen = typeof m.taskAnchorId === 'string' ? m.taskAnchorId.trim() : '';
    if (frozen) return frozen;
    return deriveTaskAnchorId(m.serverTaskId) || '';
  };
  const timestampMs = (m: ChatMessage): number => {
    const t = m.timestamp instanceof Date
      ? m.timestamp.getTime()
      : new Date(m.timestamp as unknown as string).getTime();
    return Number.isFinite(t) ? t : Number.NaN;
  };

  let changed = false;
  const out: ChatMessage[] = [];
  // Indexes (into `out`) of interrupted cards seen since the last user bubble.
  let windowCardIdxs: number[] = [];
  for (const cur of messages) {
    if (cur?.isUser) {
      windowCardIdxs = [];
      out.push(cur);
      continue;
    }
    if (isInterruptedCard(cur)) {
      let collapsed = false;
      for (const seenIdx of windowCardIdxs) {
        const seen = out[seenIdx];
        if (normalizedText(seen) !== normalizedText(cur)) continue;
        if (promptOf(seen) !== promptOf(cur)) continue;
        const seenAnchor = anchorOf(seen);
        const curAnchor = anchorOf(cur);
        if (seenAnchor && curAnchor && seenAnchor !== curAnchor) continue;
        // Same collapse semantics as the answer passes: prefer the anchored
        // copy, keep the earlier position, adopt the earlier timestamp.
        const keep = !seenAnchor && curAnchor ? cur : seen;
        const other = keep === seen ? cur : seen;
        const keepMs = timestampMs(keep);
        const otherMs = timestampMs(other);
        const adoptEarlierTs = Number.isFinite(otherMs)
          && (!Number.isFinite(keepMs) || otherMs < keepMs);
        out[seenIdx] = adoptEarlierTs ? { ...keep, timestamp: other.timestamp } : keep;
        changed = true;
        collapsed = true;
        break;
      }
      if (collapsed) continue;
      windowCardIdxs.push(out.length);
    }
    out.push(cur);
  }

  return changed ? out : messages;
}

/**
 * Point 6 (Option B) — collect in-flight server-research messages that should
 * be RE-ATTACHED (re-polled) on hydrate rather than marked interrupted. A
 * message qualifies when it carries a `serverTaskId`, has no finalized body
 * yet, and is not already interrupted. Pure (no React/storage) so the host can
 * call it inside the hydrate dispatch and kick off resume polling.
 */
export function findResumableServerTasks(
  messages: ChatMessage[],
  nowMs: number = Date.now(),
): Array<{ messageId: string; serverTaskId: string; promptPreview?: string }> {
  if (!Array.isArray(messages) || messages.length === 0) return [];
  const out: Array<{ messageId: string; serverTaskId: string; promptPreview?: string }> = [];
  messages.forEach((message, index) => {
    if (!message || message.isUser) return;
    if (message.interrupted || message.status === 'interrupted') return;
    const taskId = typeof message.serverTaskId === 'string' ? message.serverTaskId : '';
    if (!taskId) return;
    if (hasFinalizedBody(message)) return;
    // WS-4(b) (2026-09-20): a placeholder past RESUMABLE_MAX_AGE_MS is never re-attached —
    // the hydrate reconcile has marked (or will mark) it interrupted at its own timestamp.
    if (serverTaskIsExpiredForResume(taskId, nowMs)) return;
    // Nearest preceding user bubble for an eventual error/regenerate prompt.
    let promptPreview: string | undefined;
    for (let j = index - 1; j >= 0; j -= 1) {
      const prior = messages[j];
      if (prior && prior.isUser && (prior.text || '').trim()) {
        promptPreview = (prior.text || '').trim();
        break;
      }
    }
    out.push({ messageId: message.id || '', serverTaskId: taskId, promptPreview });
  });
  return out;
}

/** Identity of a server-appended background step (memory update `memext_…`,
 *  follow-up suggestions `threadfup_…`): its deterministic sidecarKey.
 *  Null for plain turn steps — those never merge across copies. */
function sidecarKeyOfUsageEntry(u: unknown): string | null {
  if (!u || typeof u !== 'object') return null;
  const key = (u as { sidecarKey?: unknown }).sidecarKey;
  return typeof key === 'string' && key.trim() ? key.trim() : null;
}

/**
 * R23.4 (2026-08-21 Miles: the message ⓘ and the activity row must ALWAYS
 * show the same steps and cost): adopt server-appended sidecar steps from a
 * remote copy of the SAME message onto the kept local copy.
 *
 * The server appends the memory-update and follow-up-suggestions steps to
 * the answer message's tokenUsages minutes after the turn (each stamped with
 * a deterministic sidecarKey). A finalized local bubble used to be terminal
 * in the sync merge, so those appended steps never reached the open pane —
 * the ⓘ under-counted exactly the background steps the activity row folds in.
 *
 * Conservative by construction:
 *   • only sidecarKey-stamped entries are adopted (a plain step that merely
 *     serialized differently between stores can never double);
 *   • adoption is keyed, so re-merging the same snapshot is a no-op;
 *   • when the local copy has NO usage at all, the remote array is adopted
 *     wholesale (nothing to lose, and the ⓘ gains the full picture).
 *
 * Returns the merged array, or null when there is nothing new. Pure.
 */
export function unionSidecarTokenUsages(
  localUsages: unknown,
  remoteUsages: unknown,
): TokenUsageMetadata[] | null {
  const remote = Array.isArray(remoteUsages) ? (remoteUsages as TokenUsageMetadata[]) : [];
  if (remote.length === 0) return null;
  const local = Array.isArray(localUsages) ? (localUsages as TokenUsageMetadata[]) : [];
  if (local.length === 0) return remote;
  const have = new Set(
    local.map(sidecarKeyOfUsageEntry).filter((k): k is string => k !== null),
  );
  const adopted = remote.filter((u) => {
    const key = sidecarKeyOfUsageEntry(u);
    return key !== null && !have.has(key);
  });
  if (adopted.length === 0) return null;
  return [...local, ...adopted];
}

/**
 * Cross-DEVICE thread sync merge (owner report 2026-07-14: a signed-in
 * browser showed an hour-old thread while an incognito session on the SAME
 * account had newer turns — thread messages had no Firestore listener, so
 * once a device held a local copy it never consulted the cloud again).
 *
 * Merges a live server snapshot of a thread transcript into the local pane
 * WITHOUT ever discarding local content:
 *   • Foreign messages (present remotely, unknown locally — turns made on
 *     another device/session) are ADOPTED and inserted by timestamp.
 *     Pending shells are adopted only when they carry a `serverTaskId` the
 *     resume machinery can drive; undriveable animation shells are skipped
 *     (they could never finalize here and would render as dead spinners).
 *   • The same logical message on both sides (matched by taskAnchorId OR id)
 *     takes the remote copy only when it is a strict UPGRADE — remote
 *     finalized, local still pending/interrupted (the server-completion
 *     convergence case). Never a downgrade.
 *   • Local-only messages are always preserved (optimistic bubbles the
 *     server hasn't acked; transcripts beyond the server's 100-message cap).
 *   • Anything a live send in THIS tab is driving is untouchable
 *     (session-hardening S1 — the live stream owns those bubbles).
 *
 * Convergence (echo-loop safety, 2026-07-12 incident class): a snapshot of
 * this device's own persisted state merges to `changed:false`, so
 * listener → dispatch → mirror-upsert → snapshot cannot cycle.
 *
 * I4 (paged thread open, 2026-07-15) — `dropForeignOlderThanMs`: when the
 * local pane holds only the NEWEST page of a long thread (older pages not yet
 * loaded), a remote snapshot can carry messages OLDER than the loaded window.
 * Adopting them would splice a fragment of old history against the window's
 * top edge with a potential GAP where the unloaded pages belong. Policy:
 * DROP (never buffer) foreign messages strictly older than the window
 * boundary — they are durably in the per-doc store (every legacy-doc writer
 * dual-writes per-docs), so they surface intact when their page is loaded via
 * "Load earlier messages" or on the next full rehydrate. Buffering was
 * rejected: an unbounded holding pen that must be invalidated on thread
 * switches/sign-out for messages the user hasn't asked to see yet.
 * In-place UPGRADES of messages already in the window (matched by anchor/id)
 * are unaffected. Callers pass the boundary ONLY while older pages exist.
 *
 * Pure; the live-send predicate is injectable for tests.
 */
export function mergeRemoteThreadMessagesIntoLocal(
  local: ChatMessage[],
  remote: ChatMessage[],
  opts: {
    isTaskLiveInThisTab?: (taskId: string) => boolean;
    /** I4: drop FOREIGN remote messages strictly older than this epoch-ms boundary. */
    dropForeignOlderThanMs?: number;
  } = {},
): { changed: boolean; messages: ChatMessage[] } {
  const base = Array.isArray(local) ? local : [];
  if (!Array.isArray(remote) || remote.length === 0) {
    return { changed: false, messages: base };
  }
  const isTaskLive = opts.isTaskLiveInThisTab ?? isLiveSendInFlight;

  const anchorOf = (m: ChatMessage): string => {
    const anchor = typeof m.taskAnchorId === 'string' ? m.taskAnchorId.trim() : '';
    return anchor || (m.id || '');
  };
  const taskIdOf = (m: ChatMessage): string | null => {
    if (typeof m.serverTaskId === 'string' && m.serverTaskId.trim()) return m.serverTaskId.trim();
    const anchor = anchorOf(m);
    if (anchor.startsWith('prest0n_task_')) return anchor.slice('prest0n_task_'.length);
    return null;
  };
  const isPendingShape = (m: ChatMessage): boolean =>
    htmlHasPendingAnimation(m.html)
    || (!!m.sourceTag && (PENDING_SOURCE_TAGS.has(m.sourceTag) || m.sourceTag.startsWith('prest0n_tool_start_')));
  const isFinalizedCopy = (m: ChatMessage): boolean =>
    !m.interrupted && m.status !== 'interrupted' && !isPendingShape(m) && hasFinalizedBody(m);

  // Index local by BOTH raw id and anchor so a local optimistic bubble
  // (client uuid id + taskAnchorId) collapses with the server's anchored
  // copy instead of duplicating it.
  const localIndexByKey = new Map<string, number>();
  // PR-F0 (2026-09-21, userRowTwins.ts): a user row's SHARED id is a key too —
  // the store's copy of the reader's own question (`prest0n_task_<id>__user`)
  // must converge on the client bubble that already names it, never insert as a
  // foreign twin under the answer.
  const localIndexBySharedId = new Map<string, number>();
  base.forEach((m, i) => {
    if (!m) return;
    if (m.id) localIndexByKey.set(m.id, i);
    const anchor = anchorOf(m);
    if (anchor) localIndexByKey.set(anchor, i);
    const shared = m.isUser === true ? sharedIdOf(m) : '';
    if (shared && isServerUserTwinId(shared)) localIndexBySharedId.set(shared, i);
  });

  // Epoch millis of a message's timestamp; NaN when invalid/absent.
  const msOf = (m: ChatMessage): number => {
    const t = m.timestamp instanceof Date
      ? m.timestamp.getTime()
      : new Date(m.timestamp as unknown as string).getTime();
    return Number.isFinite(t) && t > 0 ? t : Number.NaN;
  };

  const seqOf = (m: ChatMessage): number | undefined =>
    typeof m?.seq === 'number' && Number.isFinite(m.seq) ? m.seq : undefined;
  const seqAllowsAfter = (cur: ChatMessage, fm: ChatMessage): boolean => {
    const a = seqOf(cur);
    const b = seqOf(fm);
    return a === undefined || b === undefined ? true : a <= b;
  };

  let changed = false;
  const out: ChatMessage[] = base.slice();
  const foreign: ChatMessage[] = [];

  for (const rm of remote) {
    if (!rm || !rm.id) continue;
    const anchor = anchorOf(rm);
    let idx = localIndexByKey.get(anchor);
    if (idx === undefined) idx = localIndexByKey.get(rm.id);
    if (idx === undefined && rm.isUser === true) {
      // PR-F0: the remote is the store TWIN of a question this list holds under
      // the client's id — or the client COPY of a twin this list holds. One
      // question either way: converge on it.
      const byShared = localIndexBySharedId.get(rm.id);
      if (byShared !== undefined) {
        idx = byShared;
      } else {
        const rmShared = sharedIdOf(rm);
        if (rmShared && isServerUserTwinId(rmShared)) idx = localIndexByKey.get(rmShared);
      }
    }

    const taskId = taskIdOf(rm);
    if (taskId && isTaskLive(taskId)) continue;

    if (idx === undefined) {
      // I4 older-than-window policy (see doc above): a foreign message older
      // than the loaded page window is DROPPED, not adopted — its page loads
      // it later, converged by anchor, with no phantom gap in between.
      // Invalid-timestamp messages are NOT dropped (they append at the end,
      // never above the window boundary, so they cannot fake old history).
      if (typeof opts.dropForeignOlderThanMs === 'number') {
        const rmMs = msOf(rm);
        if (Number.isFinite(rmMs) && rmMs < opts.dropForeignOlderThanMs) continue;
      }
      const drivablePending = typeof rm.serverTaskId === 'string' && !!rm.serverTaskId.trim();
      // Terminal Interrupted cards are adopted too (2026-08-14): the server's
      // durable failure write is the single source of truth for a failed
      // turn — dropping it here left the thread with a spinner and no card.
      if (rm.isUser || isFinalizedCopy(rm) || isTerminalInterruptedCopy(rm) || drivablePending) {
        foreign.push(rm);
        changed = true;
      }
      continue;
    }

    const cur = out[idx];
    const curTaskId = taskIdOf(cur);
    if (curTaskId && isTaskLive(curTaskId)) continue;
    if (
      cur.isUser === true && rm.isUser === true && rm.id !== cur.id
      && (sharedIdOf(rm) === cur.id || sharedIdOf(cur) === rm.id)
    ) {
      // PR-F0: one question, two shapes. The CLIENT copy is the richer one (the
      // bubble's html, the turn's orderTs/seq, the reply ref): a local twin gives
      // way to it IN PLACE; a local copy keeps its place against the twin.
      if (isServerUserTwinId(cur.id) && sharedIdOf(rm) === cur.id) {
        out[idx] = rm;
        changed = true;
      }
      continue;
    }
    if (isFinalizedCopy(rm) && !isFinalizedCopy(cur)) {
      out[idx] = rm;
      changed = true;
    } else if (
      // 2026-08-14 (Harbor Ledger wedge): the server's durable Interrupted
      // card must retire a local pending/streaming placeholder for the same
      // anchor — the task is live-guarded above, so reaching here means no
      // live run owns it in this tab. Never overwrites a finalized answer or
      // a user bubble; only a still-spinning placeholder is replaced.
      isTerminalInterruptedCopy(rm)
      && !cur.isUser
      && !isFinalizedCopy(cur)
      && (isPendingShape(cur) || isInFlightPlaceholderMessage(cur))
    ) {
      out[idx] = rm;
      changed = true;
    } else if (isFinalizedCopy(rm) && isFinalizedCopy(cur) && !rm.isUser && !cur.isUser) {
      // R23.4 (2026-08-21 Miles: ⓘ ↔ activity-row parity): both copies are
      // finalized — the message itself stays LOCAL (never a body swap), but
      // server-appended background steps (memory update / follow-up
      // suggestions, sidecarKey-stamped) ride in so the open pane's ⓘ
      // converges on the same step list the activity row folds. Keyed
      // adoption ⇒ re-merging the same snapshot stays a no-op (echo-loop
      // safety preserved).
      const mergedUsages = unionSidecarTokenUsages(cur.tokenUsages, rm.tokenUsages);
      if (mergedUsages) {
        out[idx] = { ...cur, tokenUsages: mergedUsages };
        changed = true;
      }
    }
  }

  if (!changed) return { changed: false, messages: base };
  if (foreign.length === 0) return { changed: true, messages: out };

  // Insert foreign messages by timestamp so an interleaved cross-device
  // conversation reads in order. Invalid/epoch timestamps append at the end
  // (never jump the transcript); local relative order is never disturbed.
  for (const fm of foreign) {
    const fmMs = msOf(fm);
    if (Number.isNaN(fmMs)) {
      out.push(fm);
      continue;
    }
    let insertAt = out.length;
    for (let i = out.length - 1; i >= 0; i -= 1) {
      const curMs = msOf(out[i]);
      // PR-F0 (2026-09-21): at an EQUAL instant the turn's own `seq` decides
      // (question 0, answer 1 — the rule every store sort already applies), so
      // a foreign question never lands under the answer it belongs above. With
      // `seq` missing on either side the rule is today's: after.
      if (!Number.isNaN(curMs) && (curMs < fmMs || (curMs === fmMs && seqAllowsAfter(out[i], fm)))) {
        insertAt = i + 1;
        break;
      }
      insertAt = i;
    }
    out.splice(insertAt, 0, fm);
  }
  return { changed: true, messages: out };
}

/**
 * I4 (paged thread open): the loaded window's lower time boundary — the
 * oldest finite timestamp among the locally-loaded messages. Undefined when
 * the list is empty or no message carries a usable timestamp (callers must
 * then adopt everything: with no provable boundary, dropping would lose real
 * messages). Pure.
 */
export function oldestLoadedMessageTs(messages: ChatMessage[]): number | undefined {
  if (!Array.isArray(messages) || messages.length === 0) return undefined;
  let oldest: number | undefined;
  for (const m of messages) {
    if (!m) continue;
    const t = m.timestamp instanceof Date
      ? m.timestamp.getTime()
      : new Date(m.timestamp as unknown as string).getTime();
    if (!Number.isFinite(t) || t <= 0) continue;
    if (oldest === undefined || t < oldest) oldest = t;
  }
  return oldest;
}

/**
 * H1 (owner report 2026-07-14: sidebar spinner + ticking clock outlived a
 * completed deep-research turn) — pure detector for the task-completion →
 * durable-thread-write delivery path.
 *
 * When a server task's terminal write lands via the active-thread Firestore
 * listener (the Wave-4 push path) or the hydrate-recovery poll's durable
 * write, the in-flight placeholder in the pane is UPGRADED to the finalized
 * report — but that delivery path used to bypass every processing release:
 * no live onDone exists (the pre-refresh page session owned it), the
 * per-message resume effect is skipped while the recovery poll holds
 * `isPollingTask`, and `reconcileDiscoveryChips` deliberately never clears
 * the ACTIVE thread ("its own finalize owns that" — but no finalize runs on
 * this path). Result: `processingByThread[threadId]` and its startedAt/turn
 * stamps leaked until the 10-minute safety floor.
 *
 * This predicate answers: "did this merge just retire the thread's LAST
 * in-flight indication?" — i.e. the BEFORE transcript carried at least one
 * live in-flight assistant indication and the AFTER transcript carries none.
 * The host dispatches the SAME `SET_PROCESSING:false` (thread-keyed) release
 * the send path's finalize would have dispatched. Conservative by
 * construction: any surviving in-flight indication (e.g. a NEW live turn's
 * widget on the same thread) suppresses the release, so a genuinely running
 * turn is never cut short. Pure — no React, no storage.
 */
export function remoteFinalizeShouldReleaseProcessing(
  before: ChatMessage[],
  after: ChatMessage[],
): boolean {
  // Shared definition (2026-07-31): the in-flight predicate lives in ONE
  // exported helper so this detector and the convergent-lastActiveAt
  // derivation can never drift apart.
  if (!Array.isArray(before) || before.length === 0) return false;
  if (!Array.isArray(after)) return false;
  if (!before.some(isInFlightPlaceholderMessage)) return false;
  return !after.some(isInFlightPlaceholderMessage);
}

// ────────────────────────────────────────────────────────────────────────────
// SET_MESSAGES identity bail (PROD-185, 2026-08-19)
// ────────────────────────────────────────────────────────────────────────────
//
// The SET_MESSAGES reducer case always produced a NEW array — even when the
// normalized result was message-for-message identical to current state. Every
// converged re-dispatch (reconcile passes, snapshot echoes, storage re-reads)
// therefore churned `state.messages` IDENTITY, and identity churn is the fuel
// for downstream per-commit machinery — most visibly the transcript scroll
// controller's items layout effect, whose edge-state setters tripped React's
// 50-nested-update ceiling in production (sourcemap-decoded #185 crash on a
// Regenerate click). A converged dispatch must be a NO-OP at the reducer
// boundary so no cycle can sustain itself through this case.
//
// Conservative by construction: bail ONLY on confident equality — reference
// equality per element, or an explicit signature across the fields the chat
// surface renders/keys on. Any doubt (unknown field drift, rebuilt
// attachments, changed usage object) → NOT identical → normal replacement.
// A false negative merely skips the optimization; a false positive would
// swallow a real update, so every compared field is listed explicitly.

function attachmentsEffectivelyIdentical(
  a: ChatMessage['attachments'],
  b: ChatMessage['attachments'],
): boolean {
  if (a === b) return true;
  if (!Array.isArray(a) || !Array.isArray(b)) return false;
  if (a.length !== b.length) return false;
  for (let i = 0; i < a.length; i += 1) {
    if (a[i] !== b[i]) return false;
  }
  return true;
}

function messagesEffectivelyIdentical(a: ChatMessage, b: ChatMessage): boolean {
  if (a === b) return true;
  if (!a || !b) return false;
  return (
    a.id === b.id
    && a.isUser === b.isUser
    && a.text === b.text
    && a.html === b.html
    && a.status === b.status
    && a.interrupted === b.interrupted
    && a.sourceTag === b.sourceTag
    && a.serverTaskId === b.serverTaskId
    && a.taskAnchorId === b.taskAnchorId
    && a.regeneratePrompt === b.regeneratePrompt
    && a.workflowActionButtonsHtml === b.workflowActionButtonsHtml
    && a.isContextOnly === b.isContextOnly
    && a.tokenUsage === b.tokenUsage
    && a.tokenUsages === b.tokenUsages
    && attachmentsEffectivelyIdentical(a.attachments, b.attachments)
  );
}

/**
 * True when `next` carries no observable change relative to `prev` — the
 * SET_MESSAGES case then returns the previous state object untouched,
 * preserving `state.messages` identity and starving any dispatch cycle.
 */
export function messageListsEffectivelyIdentical(
  prev: ReadonlyArray<ChatMessage>,
  next: ReadonlyArray<ChatMessage>,
): boolean {
  if (prev === next) return true;
  if (!Array.isArray(prev) || !Array.isArray(next)) return false;
  if (prev.length !== next.length) return false;
  for (let i = 0; i < prev.length; i += 1) {
    if (!messagesEffectivelyIdentical(prev[i], next[i])) return false;
  }
  return true;
}
